import {
  Box,
  Divider,
  HStack,
  Icon,
  Link,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { toLower } from "lodash";
import { ReactNode, useState } from "react";
import {
  FaBullhorn,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaExclamationCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { isLoading } from "../../../../common/helper/commonHelper";
import { PersonMarketingDetails } from "../../../../common/types/person";
import CenteredTextBox from "../../../../components/CenteredTextBox";
import IButton from "../../../../components/IButton";
import ITitle from "../../../../components/ITitle";
import Pagination from "../../../../components/Pagination";
import SpinnerContainer from "../../../../components/SpinnerContainer";
import { useAppDispatch } from "../../../../store";
import {
  getEmailSent,
  selectPerson,
  setMarketingActivityPage,
} from "../personDbSlice";
import sanitizeHtml from "sanitize-html";
import { FormatDate } from "../../../../components/DateTimeRangeFilter";

const eventsName: {
  [key: string]: { title: string; icon?: ReactNode; color?: string };
} = {
  clicked: { title: "Email link clicked" },
  opened: { title: "Email opened" },
  delivered: { title: "Email delivered" },
  processed: { title: "Email processed" },
  dropped: {
    title: "Email dropped",
    icon: <FaExclamationCircle />,
    color: "brand.orange",
  },
  unsubscribed: {
    title: "Unsubscribed",
    icon: <FaTimesCircle />,
    color: "brand.red",
  },
  sent: { title: "Email sent" },
};

const SENT_EVENT_TYPE = "Sent";
const CLICK_EVENT_TYPE = "Clicked";

function EmailRender({ emailContent }: { emailContent: string }) {
  function htmlDecode(input: string) {
    var e = document.createElement("div");
    e.innerHTML = input;
    return e.childNodes.length
      ? sanitizeHtml(input, {
          allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
        })
      : "";
  }

  return (
    <Box px={3} w="100%">
      <ITitle title="Email message" fontWeight="500" color="gray.400" mb={1} />
      <Box
        minW="100%"
        bg="gray.100"
        p={4}
        wordBreak="break-all"
        borderRadius="sm"
      >
        <Box dangerouslySetInnerHTML={{ __html: htmlDecode(emailContent) }} />
      </Box>
    </Box>
  );
}

function ActivityTab({
  data,
  personId,
}: {
  data: PersonMarketingDetails;
  personId: string;
}) {
  const [expand, setExpand] = useState(false);
  const dispatch = useAppDispatch();
  const { emailSent } = useSelector(selectPerson);

  const event = eventsName[toLower(data.event)];
  const emailData = emailSent.data[data.correlation_id];

  function showEmail() {
    dispatch(getEmailSent({ personId, correlationId: data.correlation_id }));
  }

  function showDetails() {
    setExpand(!expand);
    !expand && data.event === SENT_EVENT_TYPE && !emailData && showEmail();
  }

  return (
    <Box
      borderRadius="md"
      overflow="hidden"
      w="100%"
      fontSize="14px"
      border="1px solid"
      borderColor="gray.200"
      margin="10px !important"
    >
      <HStack
        justifyContent="space-between"
        w="100%"
        p={3}
        bg="grayV2.100"
        fontWeight="600"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <HStack
          color={event && (event.color || "brand.black")}
          alignItems="center"
        >
          {event && event.icon}
          <ITitle
            title={event ? event.title : data.event}
            mt="2px !important"
          />
        </HStack>
        <FormatDate date={data.timestamp} showTime />
      </HStack>
      <HStack justifyContent="space-between" p={3}>
        <HStack>
          <Tag>
            <Icon as={FaEnvelope} color="brand.blue" />{" "}
            <Text ml={2}>{data.template_name}</Text>
          </Tag>
          <Text>from</Text>
          <Tag>
            <Icon as={FaBullhorn} color="brand.blue" />{" "}
            <Text ml={2}>{data.campaign_name}</Text>
          </Tag>
        </HStack>
        {(data.context || data.event === SENT_EVENT_TYPE) && (
          <IButton
            size="sm"
            fontSize="13px"
            rightIcon={expand ? <FaChevronUp /> : <FaChevronDown />}
            onClick={showDetails}
            name="show-hide-toggle"
          >
            {`${expand ? "Hide" : "Show"} details`}
          </IButton>
        )}
      </HStack>
      <Divider />
      {expand && (
        <VStack alignItems="flex-start" pb={5} py={3} spacing={3} w="100%">
          {data.context && (
            <Box px={3}>
              <ITitle
                title={`${data.event === CLICK_EVENT_TYPE ? "Link" : "Remark"}`}
                fontWeight="500"
                color="gray.400"
                mb={1}
              />
              {data.event === CLICK_EVENT_TYPE ? (
                <Link
                  href={data.context}
                  target="_blank"
                  maxWidth="100%"
                  wordBreak="break-all"
                >
                  {data.context}
                </Link>
              ) : (
                <Text color="brand.black"> {data.context} </Text>
              )}
            </Box>
          )}
          {data.event === SENT_EVENT_TYPE && (
            <SpinnerContainer
              loading={!emailData && isLoading(emailSent.loading)}
              width="100%"
            >
              {emailData && <EmailRender emailContent={emailData.content} />}
            </SpinnerContainer>
          )}
        </VStack>
      )}
    </Box>
  );
}

export default function PersonMarketingActivities({
  personId,
}: {
  personId: string;
}) {
  const dispatch = useAppDispatch();
  const { personMarketingActivity } = useSelector(selectPerson);

  return (
    <VStack w="100%">
      <SpinnerContainer
        loading={personMarketingActivity.fetchingList}
        height="500px"
      >
        {personMarketingActivity.totalPageCount &&
        personMarketingActivity.list?.length ? (
          personMarketingActivity.list.map((activity, index) => (
            <ActivityTab data={activity} key={index} personId={personId} />
          ))
        ) : (
          <CenteredTextBox color="gray.400" message="No marketing activities" />
        )}
        {!!personMarketingActivity.totalPageCount && (
          <Pagination
            currentPage={personMarketingActivity.currentPageNo}
            onPageChange={(pageNo: number) =>
              dispatch(setMarketingActivityPage(pageNo))
            }
            pageCount={personMarketingActivity.totalPageCount}
          />
        )}
      </SpinnerContainer>
    </VStack>
  );
}
